import './style.modules.scss'

import React from 'react'

/** Props for the {@link Toggle} switch. */
export type ToggleProps = {
  /** Unique id linking the label, input and accessible switch. */
  id: string
  /** Current on/off state (controlled). */
  checked: boolean
  /** Change handler fired when the switch is toggled. */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Base test id; child elements are suffixed with `-input` and `-slider`. */
  testId?: string
  /** Extra class names appended to the switch wrapper. */
  className?: string
  /** Optional analytics attribute set on the underlying input. */
  dataAnalytics?: string
  /** Disables the switch: not focusable, not interactive, and dimmed. */
  disabled?: boolean
  /** Accessible label content, visually hidden and referenced by the switch. */
  children?: React.ReactNode
}

/**
 * An on/off toggle switch. Controlled via `checked`/`onChange`, focusable and
 * operable with Space/Enter.
 */
export const Toggle: React.FC<ToggleProps> = ({
  id,
  checked,
  onChange,
  testId,
  className = '',
  dataAnalytics,
  disabled = false,
  children
}) => {
  // Keyboard handler for accessibility: activate the hidden input on Space/Enter.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (disabled) {
      return
    }
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      // Synthesize a change event for the input
      const input = document.getElementById(id) as HTMLInputElement | null
      if (input) {
        input.click()
      }
    }
  }
  const wrapperClassName = [
    'Toggle',
    checked ? 'Toggle--on' : '',
    disabled ? 'Toggle--disabled' : '',
    className
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <label htmlFor={id} className={wrapperClassName} data-testid={testId}>
      {children ? (
        <span className="u-hiddenVisually">{children}</span>
      ) : (
        <span className="u-hiddenVisually">Toggle</span>
      )}
      <input
        data-testid={testId ? `${testId}-input` : undefined}
        id={id}
        type="checkbox"
        className={'Toggle-input'}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        data-analytics={dataAnalytics}
        tabIndex={-1} // Hide native input from tab order, handled by custom element
      />
      <span
        data-testid={testId ? `${testId}-slider` : undefined}
        className={'Toggle-slider'}
        role="switch"
        tabIndex={disabled ? -1 : 0}
        aria-checked={checked}
        aria-disabled={disabled || undefined}
        aria-labelledby={id}
        onKeyDown={handleKeyDown}
      />
    </label>
  )
}
